import type { EAction, WebSockets } from '@pushflow/types'
import { clsx } from 'clsx'
import { AnimatePresence } from 'framer-motion'
import { useAtomValue } from 'jotai'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import dealerCoin from '../../../assets/images/dealer-coin.svg'
import { userSettingsAtom } from '../../../store/atoms'
import { hidden } from '../../../theme.css'
import type { PlayerType } from '../../../types'
import type { ECurrency, ERound } from '../../../types/enums'
import { getOffsetSeat } from '../../../utils'
import { Coin } from '../../atoms'
import { Money, PlayerPlug } from '../../molecules'
import type { PlayerResultsType } from '../../templates/PokerRoom/types'
import { ChatOnTable, TablePlayer } from '..'
import {
  betBlock,
  betBlockBySeatVariant,
  chipStyle,
  dealerCoinBlock,
  playerCellBySeat,
  playerGridAreaBySeat,
  playerInfoBlock,
  playerInfoBlockVariants,
  playersBlock,
  tableContentGrid,
} from './index.css'

export const TablePlayers = <GPlugMode extends 'sit' | 'invite'>(props: {
  userId: string | null
  userSeat: number | null
  offset?: number
  round: ERound
  turnSeconds: number
  turnRemainSeconds: number | null
  playerTurnId: string | null
  dealerSeat: number | null
  players: PlayerType[]
  winners: WebSockets.WinnerType[]
  communityCards: string | null
  secondBoard: string | null
  maxPlayers: number
  bigBlind: number
  currency: ECurrency
  plugMode?: GPlugMode
  handNumber: number
  isSnapshot?: boolean
  renderUserPlayer: boolean
  chatMessage?: {
    userId: string
    text: string
  }
  playerResults: PlayerResultsType
  onClickPlayerPlug?: (action: GPlugMode, seat: number) => unknown
  onPlayerMove?: (userId: string, move: EAction) => void
  onPlayerWin?: (userId: string) => void
  children?: React.ReactNode
}) => {
  const { t } = useTranslation()
  const userSettings = useAtomValue(userSettingsAtom)

  const freeSeats = useMemo(() => {
    if (props.isSnapshot) {
      return []
    }
    const allSeats = Array.from({ length: props.maxPlayers }, (_, seat) => seat)
    return allSeats.filter(
      (seat) => !props.players.some((player) => player.seat === seat),
    )
  }, [props.isSnapshot, props.maxPlayers, props.players])

  const calculatedDealerSeat = useMemo(() => {
    if (props.dealerSeat === null) return null
    if (props.dealerSeat === props.userSeat) return 'user'
    return getOffsetSeat(props.dealerSeat, props.offset, props.maxPlayers)
  }, [props.dealerSeat, props.userSeat, props.offset, props.maxPlayers])

  const renderDealerCoin = () => {
    if (calculatedDealerSeat === null) return null

    return (
      <img
        src={dealerCoin}
        className={clsx(
          dealerCoinBlock,
          playerGridAreaBySeat[calculatedDealerSeat],
        )}
        alt={t('table.alt.dealer')}
      />
      // <>
      //   {Array.from({ length: props.maxPlayers }, (_, seat) => seat).map(
      //     (seat) => (
      //       <img
      //         key={seat}
      //         src={dealerCoin}
      //         className={playerInfoBySeat[seat]}
      //         alt='Dealer'
      //       />
      //     ),
      //   )}
      // </>
    )
  }

  const renderPlayers = () => {
    return props.players.map((player) => {
      const isUserPlayer = props.userId === player.userId
      const seat = isUserPlayer
        ? 'user'
        : getOffsetSeat(player.seat, props.offset, props.maxPlayers)
      const isCurrentTurn = player.userId === props.playerTurnId
      const faceDirection =
        seat === 'user' || seat <= 2 || seat >= 7 ? 'right' : 'left'
      const handName = props.playerResults[player.userId]
        ? isUserPlayer
          ? props.playerResults[player.userId].hands.first?.handName
          : props.playerResults[player.userId].hands.strongest?.handName
        : null

      return (
        <div
          key={player.userId}
          className={playerCellBySeat[seat]}
        >
          <TablePlayer
            userId={player.userId}
            name={player.name}
            avatar={player.avatar}
            cards={player.cards}
            bet={player.bet}
            buyIn={player.buyIn}
            bigBlind={props.bigBlind}
            move={player.move}
            seat={player.seat}
            faceDirection={faceDirection}
            handName={handName}
            round={props.round}
            turnSeconds={props.turnSeconds}
            turnTimeout={
              isCurrentTurn && !props.isSnapshot
                ? props.turnRemainSeconds
                : null
            }
            communityCards={props.communityCards}
            status={player.status}
            handNumber={props.handNumber}
            winAmount={props.playerResults?.[player.userId]?.winAmount}
            isOnline={player.isOnline}
            isLeaving={player.isLeaving}
            isCurrentTurnPlayer={isCurrentTurn}
            isWinner={props.playerResults?.[player.userId]?.isWinner}
            isSnapshot={props.isSnapshot}
            className={
              isUserPlayer && !props.renderUserPlayer ? hidden : undefined
            }
            onMove={(move) =>
              props.onPlayerMove
                ? props.onPlayerMove(player.userId, move)
                : null
            }
            onWin={() =>
              props.onPlayerWin ? props.onPlayerWin(player.userId) : null
            }
          />
        </div>
      )
    })
  }

  const renderPlayerPlugs = () => {
    const { plugMode } = props
    if (!plugMode) return null

    return freeSeats.map((freeSeat) => {
      const seat = getOffsetSeat(freeSeat, props.offset, props.maxPlayers)

      return (
        <div
          key={freeSeat}
          className={playerCellBySeat[seat]}
        >
          <PlayerPlug
            mode={plugMode}
            onClick={() => props.onClickPlayerPlug?.(plugMode, seat)}
          />
        </div>
      )
    })
  }

  return (
    <div className={playersBlock}>
      {props.isSnapshot ? (
        renderPlayers()
      ) : (
        <AnimatePresence>{renderPlayers()}</AnimatePresence>
      )}
      <div className={tableContentGrid}>
        {props.players.map((player) => {
          const isUserPlayer = props.userId === player.userId
          const seat = isUserPlayer
            ? 'user'
            : (getOffsetSeat(player.seat, props.offset, props.maxPlayers) as
                | 0
                | 1
                | 2
                | 3
                | 4
                | 5
                | 6
                | 7
                | 8)

          return (
            <div
              key={player.userId}
              className={clsx(
                playerInfoBlock,
                playerGridAreaBySeat[seat],
                playerInfoBlockVariants[seat],
              )}
            >
              <div
                className={clsx(
                  betBlock,
                  betBlockBySeatVariant[seat],
                  player.bet === 0 ? hidden : null,
                )}
              >
                <Coin
                  color='blue'
                  className={chipStyle}
                />
                <span>
                  <Money
                    inBigBlinds={userSettings.valuesInBigBlinds}
                    bigBlind={props.bigBlind}
                  >
                    {player.bet}
                  </Money>
                </span>
              </div>
              {props.dealerSeat === player.seat ? renderDealerCoin() : null}
            </div>
          )
        })}
        {props.children}
      </div>
      {renderPlayerPlugs()}
      {props.isSnapshot ? null : (
        <ChatOnTable
          newMessage={props.chatMessage}
          players={props.players}
          offset={props.offset}
          maxPlayers={props.maxPlayers}
          currentUserId={props.userId}
        />
      )}
    </div>
  )
}
